import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// COMPONENTS
import FormInputBooleanSwitch from "../../../../components/form/FormInputBooleanSwitch";
import Ln2MappingEditor, {
  Ln2MappingData,
  defaultLn2MappingData,
} from "../../../../components/adapters/Ln2MappingEditor";
import CdoOpcCommand from "./CdoOpcCommand";
// FLATBUFFERS
import { LiftType } from "../../../../../flatbuffers/generated/archive/lift-type";
// INTERFACES
import {
  OpcMappingsInterface,
  OpcCmdToMessageInterface,
  OpcCommandType,
  ResponseContentTypesInterface,
  ReadContentTypesInterface,
  WriteContentTypesInterface,
  OpcCommand,
} from "../../../../../interfaces/AdapterCdoOpc.interface";

export default function CdoOpcMappingOpcCmdToMsg({
  setValue,
  opcMapping,
}: {
  setValue: (newValue: any) => void;
  opcMapping: OpcMappingsInterface;
}) {
  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [opcCmdToMessage, setOpcCmdToMessage] = useState<{
    command: OpcCommand;
    opcCommand: OpcCommandType;
    address: string;
    description: string;
    ln2Mapping: Ln2MappingData;
    active: boolean;
    position: number;
    responseContentType: ResponseContentTypesInterface;
    readContentType: ReadContentTypesInterface;
    writeContentType: WriteContentTypesInterface;
  }>({
    command: {},
    opcCommand: "read_content_table",
    address: "",
    description: "",
    ln2Mapping: defaultLn2MappingData(),
    position: 0,
    active: true,
    responseContentType: "read_item_response",
    readContentType: "read_sync_item_message",
    writeContentType: "make_group",
  });

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChangeOpcCmdToMsgActive = (newValue: boolean, _variableName: string) => {
    const copy = structuredClone(opcCmdToMessage);
    copy.active = newValue;
    setOpcCmdToMessage(copy);
  };

  const onClickAddNewOpcCmdMsg = () => {
    const msg = opcCmdToMessage.ln2Mapping.message;
    const newOpcCmdMessage: OpcCmdToMessageInterface = {
      key: {
        [opcCmdToMessage.opcCommand]:
          opcCmdToMessage.command.read_content_table ||
          opcCmdToMessage.command.response_content_table ||
          opcCmdToMessage.command.write_content_table,
      },
      value: {
        encoded_type: msg?.encodedType ?? 0,
        message_content: msg?.messageContent ?? 0,
        message_value: msg?.messageValue ?? 0,
        fbs_device_type: msg?.deviceType ?? LiftType.ELEVATOR,
        active: opcCmdToMessage.active,
        position: opcCmdToMessage.position,
      },
    };
    const copy = structuredClone(opcMapping);
    copy.opc_cmd_to_msg.push(newOpcCmdMessage);
    setValue(copy);
    setOpcCmdToMessage({
      command: {},
      opcCommand: "read_content_table",
      address: "",
      description: "",
      ln2Mapping: defaultLn2MappingData(),
      position: 0,
      active: true,
      responseContentType: "read_item_response",
      readContentType: "read_sync_item_message",
      writeContentType: "add_item",
    });
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <CdoOpcCommand setOpcCommand={setOpcCmdToMessage} opcCommand={opcCmdToMessage} />
      <Ln2MappingEditor
        value={opcCmdToMessage.ln2Mapping}
        onChange={(updated) => setOpcCmdToMessage((prev) => ({ ...prev, ln2Mapping: updated }))}
        disabled={false}
        hideBankLift
        renderExtraLn2Fields={() => (
          <FormInputBooleanSwitch
            value={opcCmdToMessage.active}
            onChangeSwitch={onChangeOpcCmdToMsgActive}
            labelName={t("general.active")}
            variableName="active"
            isRequired
            disabledInput={false}
          />
        )}
      />
      <div className="col-md-12 mt-3">
        <div className="generalStyles__flex">
          <div className="generalStyles__inputFlexNameLarge">
            <span className="generalStyles__inputFlexRequired" />
          </div>
          <div className="generalStyles__width100">
            <Button
              onClick={onClickAddNewOpcCmdMsg}
              className="buttonStyle__28 generalStyles__width100"
            >
              <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
              {t("general.add")}
            </Button>
          </div>
          <div className="generalStyles__infoTooltipIconBlank" />
        </div>
      </div>
    </div>
  );
}
